import React, { useEffect, useState } from "react";
import {
    BookOpen,
    Edit3,
    Loader2,
    Plus,
    RefreshCw,
    Search,
    Trash2
} from "lucide-react";
import {
    createKbArticle,
    createKbCategory,
    deleteKbArticle,
    getKbArticles,
    getKbCategories,
    updateKbArticle,
    updateKbCategory
} from "../services/knowledgeBaseService";
import "./KnowledgeBaseAdminPage.css";
import PageShell from "../components/layout/PageShell";

const emptyArticle = {
    categoryId: "",
    slug: "",
    title: "",
    summary: "",
    bodyHtml: "",
    audience: "all",
    status: "draft",
    tagsCsv: "",
    sortOrder: 0
};

const emptyCategory = {
    key: "",
    name: "",
    description: "",
    visibility: "public",
    sortOrder: 0,
    isActive: true
};

export default function KnowledgeBaseAdminPage() {
    const [categories, setCategories] = useState([]);
    const [articles, setArticles] = useState([]);
    const [totalCount, setTotalCount] = useState(0);

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");

    const [search, setSearch] = useState("");
    const [status, setStatus] = useState("");
    const [categoryId, setCategoryId] = useState("");

    const [articleModalOpen, setArticleModalOpen] = useState(false);
    const [editingArticleId, setEditingArticleId] = useState("");
    const [articleForm, setArticleForm] = useState({ ...emptyArticle });

    const [categoryModalOpen, setCategoryModalOpen] = useState(false);
    const [editingCategoryId, setEditingCategoryId] = useState("");
    const [categoryForm, setCategoryForm] = useState({ ...emptyCategory });

    useEffect(() => {
        loadAll();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [categoryId, status]);

    const loadAll = async () => {
        setLoading(true);
        setError("");

        try {
            const [categoryData, articleData] = await Promise.all([
                getKbCategories(true),
                getKbArticles({
                    search,
                    status,
                    categoryId,
                    page: 1,
                    pageSize: 100
                })
            ]);

            setCategories(categoryData || []);
            setArticles(articleData?.items || []);
            setTotalCount(articleData?.totalCount || 0);
        } catch (err) {
            setError(err?.response?.data?.message || err?.message || "Unable to load KB.");
        } finally {
            setLoading(false);
        }
    };

    const openCreateArticle = () => {
        setEditingArticleId("");
        setArticleForm({
            ...emptyArticle,
            categoryId: categories[0]?.id || ""
        });
        setArticleModalOpen(true);
    };

    const openEditArticle = (article) => {
        setEditingArticleId(article.id);
        setArticleForm({
            categoryId: article.categoryId || "",
            slug: article.slug || "",
            title: article.title || "",
            summary: article.summary || "",
            bodyHtml: article.bodyHtml || "",
            audience: article.audience || "all",
            status: article.status || "draft",
            tagsCsv: article.tagsCsv || "",
            sortOrder: article.sortOrder || 0
        });
        setArticleModalOpen(true);
    };

    const saveArticle = async (e) => {
        e.preventDefault();

        if (!articleForm.categoryId || !articleForm.title.trim()) {
            setError("Category and title are required.");
            return;
        }

        setSaving(true);
        setError("");

        try {
            if (editingArticleId) {
                await updateKbArticle(editingArticleId, articleForm);
            } else {
                await createKbArticle(articleForm);
            }

            setArticleModalOpen(false);
            await loadAll();
        } catch (err) {
            setError(err?.response?.data?.message || err?.message || "Unable to save article.");
        } finally {
            setSaving(false);
        }
    };

    const archiveArticle = async (articleId) => {
        if (!window.confirm("Archive this article?")) return;

        setSaving(true);
        setError("");

        try {
            await deleteKbArticle(articleId);
            await loadAll();
        } catch (err) {
            setError(err?.response?.data?.message || err?.message || "Unable to archive article.");
        } finally {
            setSaving(false);
        }
    };

    const openCreateCategory = () => {
        setEditingCategoryId("");
        setCategoryForm({ ...emptyCategory });
        setCategoryModalOpen(true);
    };

    const openEditCategory = (category) => {
        setEditingCategoryId(category.id);
        setCategoryForm({
            key: category.key || "",
            name: category.name || "",
            description: category.description || "",
            visibility: category.visibility || "public",
            sortOrder: category.sortOrder || 0,
            isActive: category.isActive !== false
        });
        setCategoryModalOpen(true);
    };

    const saveCategory = async (e) => {
        e.preventDefault();

        if (!categoryForm.name.trim()) {
            setError("Category name is required.");
            return;
        }

        setSaving(true);
        setError("");

        try {
            if (editingCategoryId) {
                await updateKbCategory(editingCategoryId, categoryForm);
            } else {
                await createKbCategory(categoryForm);
            }

            setCategoryModalOpen(false);
            await loadAll();
        } catch (err) {
            setError(err?.response?.data?.message || err?.message || "Unable to save category.");
        } finally {
            setSaving(false);
        }
    };

    const updateArticleField = (key, value) => {
        setArticleForm((prev) => ({ ...prev, [key]: value }));
    };

    const updateCategoryField = (key, value) => {
        setCategoryForm((prev) => ({ ...prev, [key]: value }));
    };

    const selectedCategory = categories.find((x) => x.id === categoryId);

    return (
        <PageShell title="Knowledge Base Admin">
            <div className="kb-admin-page">
                <div className="kb-admin-hero">
                    <div>
                        <span>Knowledge Base</span>
                        <h1>Help Center Manager</h1>
                        <p>Create public, CRM, and internal help articles for users and support teams.</p>
                    </div>

                    <div className="kb-admin-actions">
                        <button className="kb-btn secondary" onClick={loadAll} disabled={loading}>
                            <RefreshCw size={16} />
                            Refresh
                        </button>

                        <button className="kb-btn secondary" onClick={openCreateCategory} disabled={saving}>
                            <Plus size={16} />
                            Category
                        </button>

                        <button className="kb-btn primary" onClick={openCreateArticle} disabled={saving}>
                            <Plus size={16} />
                            Article
                        </button>
                    </div>
                </div>

                {error && <div className="kb-error">{error}</div>}

                <div className="kb-admin-grid">
                    <aside className="kb-panel">
                        <div className="kb-panel-head">
                            <h2>Categories</h2>
                        </div>

                        {categories.length === 0 ? (
                            <div className="kb-empty small">
                                <BookOpen size={28} />
                                <p>No categories found.</p>
                            </div>
                        ) : (
                            categories.map((category) => (
                                <button
                                    key={category.id}
                                    type="button"
                                    className={`kb-category-row ${categoryId === category.id ? "active" : ""}`}
                                    onClick={() => {
                                        setCategoryId((prev) =>
                                            prev === category.id ? "" : category.id
                                        );
                                    }}
                                >
                                    <div>
                                        <strong>{category.name}</strong>
                                        <span>{category.visibility} · {category.articleCount} articles</span>
                                    </div>

                                    <Edit3
                                        size={15}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            openEditCategory(category);
                                        }}
                                    />
                                </button>
                            ))
                        )}
                    </aside>

                    <main className="kb-panel">
                        <div className="kb-filters">
                            <form
                                className="kb-search"
                                onSubmit={(e) => {
                                    e.preventDefault();
                                    loadAll();
                                }}
                            >
                                <Search size={16} />
                                <input
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    placeholder="Search articles..."
                                />
                            </form>

                            <select value={status} onChange={(e) => setStatus(e.target.value)}>
                                <option value="">All Status</option>
                                <option value="draft">Draft</option>
                                <option value="published">Published</option>
                                <option value="archived">Archived</option>
                            </select>

                            <button type="button" className="kb-btn secondary" onClick={loadAll}>
                                Apply
                            </button>
                        </div>

                        {selectedCategory && (
                            <div className="kb-filter-chip">
                                Filtered by: <strong>{selectedCategory.name}</strong>
                                <button type="button" onClick={() => setCategoryId("")}>
                                    Clear
                                </button>
                            </div>
                        )}

                        {loading ? (
                            <div className="kb-loading">
                                <Loader2 className="spin" size={24} />
                                Loading articles...
                            </div>
                        ) : articles.length === 0 ? (
                            <div className="kb-empty">
                                <BookOpen size={36} />
                                <h3>No articles found</h3>
                                <p>Create your first KB article.</p>
                            </div>
                        ) : (
                            <div className="kb-article-list">
                                {articles.map((article) => (
                                    <div className="kb-article-row" key={article.id}>
                                        <div>
                                            <span>{article.categoryName || "Article"}</span>
                                            <h3>{article.title}</h3>
                                            <p>{article.summary}</p>

                                            <div className="kb-meta">
                                                <b>{article.status}</b>
                                                <b>{article.audience}</b>
                                                <b>{article.viewCount} views</b>
                                            </div>
                                        </div>

                                        <div className="kb-row-actions">
                                            <button type="button" onClick={() => openEditArticle(article)}>
                                                <Edit3 size={16} />
                                            </button>

                                            <button type="button" onClick={() => archiveArticle(article.id)}>
                                                <Trash2 size={16} />
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}

                        <div className="kb-count">Total: {totalCount}</div>
                    </main>
                </div>

                {articleModalOpen && (
                    <div className="kb-modal-backdrop">
                        <div className="kb-modal wide">
                            <div className="kb-modal-head">
                                <h2>{editingArticleId ? "Edit Article" : "Create Article"}</h2>
                                <button type="button" onClick={() => setArticleModalOpen(false)}>×</button>
                            </div>

                            <form onSubmit={saveArticle} className="kb-form">
                                <div className="kb-form-grid">
                                    <label>
                                        Category
                                        <select
                                            value={articleForm.categoryId}
                                            onChange={(e) => updateArticleField("categoryId", e.target.value)}
                                        >
                                            <option value="">Select category</option>
                                            {categories.map((category) => (
                                                <option key={category.id} value={category.id}>
                                                    {category.name}
                                                </option>
                                            ))}
                                        </select>
                                    </label>

                                    <label>
                                        Status
                                        <select
                                            value={articleForm.status}
                                            onChange={(e) => updateArticleField("status", e.target.value)}
                                        >
                                            <option value="draft">Draft</option>
                                            <option value="published">Published</option>
                                            <option value="archived">Archived</option>
                                        </select>
                                    </label>

                                    <label>
                                        Audience
                                        <select
                                            value={articleForm.audience}
                                            onChange={(e) => updateArticleField("audience", e.target.value)}
                                        >
                                            <option value="all">All</option>
                                            <option value="public">Public</option>
                                            <option value="client">Client</option>
                                            <option value="agency">Agency</option>
                                            <option value="admin">Admin</option>
                                            <option value="internal">Internal</option>
                                        </select>
                                    </label>

                                    <label>
                                        Sort Order
                                        <input
                                            type="number"
                                            value={articleForm.sortOrder}
                                            onChange={(e) =>
                                                updateArticleField("sortOrder", Number(e.target.value))
                                            }
                                        />
                                    </label>
                                </div>

                                <label>
                                    Title
                                    <input
                                        value={articleForm.title}
                                        onChange={(e) => updateArticleField("title", e.target.value)}
                                    />
                                </label>

                                <label>
                                    Slug
                                    <input
                                        value={articleForm.slug}
                                        onChange={(e) => updateArticleField("slug", e.target.value)}
                                        placeholder="auto-generated if empty"
                                    />
                                </label>

                                <label>
                                    Summary
                                    <textarea
                                        rows={3}
                                        value={articleForm.summary}
                                        onChange={(e) => updateArticleField("summary", e.target.value)}
                                    />
                                </label>

                                <label>
                                    Body HTML
                                    <textarea
                                        rows={12}
                                        value={articleForm.bodyHtml}
                                        onChange={(e) => updateArticleField("bodyHtml", e.target.value)}
                                        placeholder="<p>Your help article content...</p>"
                                    />
                                </label>

                                <label>
                                    Tags CSV
                                    <input
                                        value={articleForm.tagsCsv}
                                        onChange={(e) => updateArticleField("tagsCsv", e.target.value)}
                                        placeholder="generate, preview, publish"
                                    />
                                </label>

                                <div className="kb-modal-actions">
                                    <button
                                        type="button"
                                        className="kb-btn secondary"
                                        onClick={() => setArticleModalOpen(false)}
                                    >
                                        Cancel
                                    </button>

                                    <button type="submit" className="kb-btn primary" disabled={saving}>
                                        {saving ? "Saving..." : "Save Article"}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}

                {categoryModalOpen && (
                    <div className="kb-modal-backdrop">
                        <div className="kb-modal">
                            <div className="kb-modal-head">
                                <h2>{editingCategoryId ? "Edit Category" : "Create Category"}</h2>
                                <button type="button" onClick={() => setCategoryModalOpen(false)}>×</button>
                            </div>

                            <form onSubmit={saveCategory} className="kb-form">
                                <label>
                                    Name
                                    <input
                                        value={categoryForm.name}
                                        onChange={(e) => updateCategoryField("name", e.target.value)}
                                    />
                                </label>

                                <label>
                                    Key
                                    <input
                                        value={categoryForm.key}
                                        onChange={(e) => updateCategoryField("key", e.target.value)}
                                        placeholder="auto-generated if empty"
                                    />
                                </label>

                                <label>
                                    Description
                                    <textarea
                                        rows={4}
                                        value={categoryForm.description}
                                        onChange={(e) => updateCategoryField("description", e.target.value)}
                                    />
                                </label>

                                <div className="kb-form-grid">
                                    <label>
                                        Visibility
                                        <select
                                            value={categoryForm.visibility}
                                            onChange={(e) => updateCategoryField("visibility", e.target.value)}
                                        >
                                            <option value="public">Public</option>
                                            <option value="crm">CRM</option>
                                            <option value="internal">Internal</option>
                                        </select>
                                    </label>

                                    <label>
                                        Sort Order
                                        <input
                                            type="number"
                                            value={categoryForm.sortOrder}
                                            onChange={(e) =>
                                                updateCategoryField("sortOrder", Number(e.target.value))
                                            }
                                        />
                                    </label>
                                </div>

                                <label className="kb-checkbox">
                                    <input
                                        type="checkbox"
                                        checked={categoryForm.isActive}
                                        onChange={(e) =>
                                            updateCategoryField("isActive", e.target.checked)
                                        }
                                    />
                                    Active
                                </label>

                                <div className="kb-modal-actions">
                                    <button
                                        type="button"
                                        className="kb-btn secondary"
                                        onClick={() => setCategoryModalOpen(false)}
                                    >
                                        Cancel
                                    </button>

                                    <button type="submit" className="kb-btn primary" disabled={saving}>
                                        {saving ? "Saving..." : "Save Category"}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}
            </div>
        </PageShell>
    );
}